import { InteractionType } from "@azure/msal-browser";
import { useIsAuthenticated, useMsalAuthentication } from "@azure/msal-react";
import { Box, Typography } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes } from "react-router-dom";
import { ConfigurationError } from "./components/configurationError";
import { ErrorPage } from "./components/errorPage";
import { Home } from "./features/home";
import { TradeMatcher } from "./features/tradeMatcher";
import { TradeMatcherMenu } from "./features/tradeMatcher/menu";
import { Content } from "./layout/content";
import { MenuBar } from "./layout/menu";
import { NavBar } from "./layout/navBar";
import { Topbar } from "./layout/topbar";

type Props = {
  loginRequest: {
    scopes: string[];
  };
};

export const ConfigurationErrorApp = ({ message }: { message: string }) => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "hidden",
      }}
    >
      <Topbar />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "calc(100vh - 64px)",
        }}
      >
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <NavBar />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <MenuBar>
              <Routes>
                <Route path="tradematcher/*" element={<TradeMatcherMenu />} />
              </Routes>
            </MenuBar>
            <Content>
              <ConfigurationError message={message} />
            </Content>
          </Box>
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

const App = ({ loginRequest }: Props) => {
  useMsalAuthentication(InteractionType.Redirect, loginRequest);

  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          textAlign: "center",
          gap: 2,
        }}
      >
        <Typography variant="h4" color="primary">
          Authenticating...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we log you into Compliance Hub.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: "hidden",
        }}
      >
        <Topbar />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "calc(100vh - 64px)",
          }}
        >
          {/*
            Sidebar routes - only if you want to use a left sidebar

            <Routes>
              <Route path="/" element={<Sidebar />} />
            </Routes> */}
          <ErrorBoundary FallbackComponent={ErrorPage}>
            <NavBar />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MenuBar>
                <Routes>
                  <Route path="tradematcher/*" element={<TradeMatcherMenu />} />
                </Routes>
              </MenuBar>
              <Content>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="home/*" element={<Home />} />
                  <Route path="tradematcher/*" element={<TradeMatcher />} />
                  <Route
                    path="redirect"
                    element={<Navigate to="/home" replace />}
                  />
                  <Route path="*" element={<Navigate to="/home" replace />} />
                </Routes>
              </Content>
            </Box>
          </ErrorBoundary>
        </Box>
      </Box>
    </>
  );
};

export default App;
